// 2026-10-07 · Fichaje que «toca» al identificarse un empleado en la tablet.
// Regla única para TODOS los caminos que identifican (kiosko con o sin PIN,
// «Cambiar empleado», dispositivo asignado): con el ajuste del restaurante
// «Al identificarse, abrir el fichaje si toca entrar o salir» (requireClockIn)
// se abre el modal de fichaje si:
//   · «in»: su turno ya empezó (o empieza en ≤ 15 min) y no ha fichado;
//   · «out»: su turno ya terminó y sigue fichado.
// El cálculo lo hace el API al momento (GET employee/{id}/clocking-due) con la
// misma regla que `clockingDue` de la lista de empleados (que puede ser vieja).
// NUNCA para el admin, ni en autorizaciones con PIN, ni en el botón «Fichar»
// (que ya abre su propio modal).
import axios from "src/utils/axios";
import {
  changeOpenClockingModal,
  selectClockingByIdSuccess,
  setClockingDueNotice,
} from "src/redux/slices/employee";

const SLICE_ACTIONS = { changeOpenClockingModal, selectClockingByIdSuccess, setClockingDueNotice };

const pad = (n) => String(n).padStart(2, "0");
const hhmm = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Pide el fichaje pendiente al API; si falla, usa el de la lista (revalidado con la hora). */
export async function fetchClockingDue(employee, { now = Date.now(), http = axios } = {}) {
  if (!employee?.id || employee.isAdmin) return null;
  try {
    const res = await http.get(`employee/${employee.id}/clocking-due`);
    return res?.status === 200 && res.data?.kind ? res.data : null;
  } catch {
    return stillDue(employee.clockingDue, now);
  }
}

/** El `clockingDue` de la lista se calculó al leerla: se descarta si ya no aplica. */
export function stillDue(due, now = Date.now()) {
  if (!due?.kind) return null;
  const start = new Date(due.scheduledStart).getTime();
  const end = new Date(due.scheduledEnd).getTime();
  if (due.kind === "in") return now >= start - 15 * 60 * 1000 && now < end ? due : null;
  if (due.kind === "out") return now >= end ? due : null;
  return null;
}

export function shouldAutoOpenClocking({ user, employee, due }) {
  return !!user?.requireClockIn && !!employee && !employee.isAdmin && !!due?.kind;
}

/** Texto corto que explica por qué se abre el fichaje. */
export function clockingDueText(due, now = Date.now()) {
  if (!due?.kind) return "";
  if (due.kind === "out") return `Tu turno terminó a las ${hhmm(due.scheduledEnd)} y sigues fichado.`;
  return new Date(due.scheduledStart).getTime() > now
    ? `Tu turno empieza a las ${hhmm(due.scheduledStart)}.`
    : `Tu turno empezó a las ${hhmm(due.scheduledStart)}.`;
}

const shownKey = (employeeId, due) => `tmo:clockingDueShown:${employeeId}:${due.workItemId}:${due.kind}`;

/**
 * Tras identificar a `employee` en la tablet: si toca fichar, selecciona ese
 * turno en el modal, deja la explicación y lo abre. `once` (identificación
 * automática, p. ej. dispositivo asignado al recargar) evita reabrirlo en la
 * misma pestaña para el mismo turno y acción.
 * `actions`/`fetchDue` se pueden inyectar en los tests.
 */
export async function openClockingIfDue({ dispatch, user, employee, actions = SLICE_ACTIONS, once = false, fetchDue = fetchClockingDue }) {
  if (!user?.requireClockIn || !employee || employee.isAdmin) return false;
  const due = await fetchDue(employee);
  if (!shouldAutoOpenClocking({ user, employee, due })) return false;
  if (once) {
    try {
      if (window.sessionStorage.getItem(shownKey(employee.id, due))) return false;
      window.sessionStorage.setItem(shownKey(employee.id, due), "1");
    } catch { /* sin almacenamiento: se abre igual */ }
  }
  if (actions.selectClockingByIdSuccess) dispatch(actions.selectClockingByIdSuccess(due.workItemId));
  dispatch(actions.setClockingDueNotice(clockingDueText(due)));
  dispatch(actions.changeOpenClockingModal(true));
  return true;
}
